import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { NavLink, useNavigate } from "react-router";
import { api } from "../api/client";
import { qk, useProjects } from "../api/queries";
import { useCurrentUser } from "../lib/auth";
import { cx } from "../lib/format";
import { Icon } from "./Icon";
import { NotificationsBell } from "./NotificationsBell";

const buildSha = import.meta.env.ATR_GIT_SHA;

export function Sidebar({ pid, onNavigate }: { pid: string | undefined; onNavigate?: () => void }) {
  const me = useCurrentUser();
  const { data: projects } = useProjects();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const logout = useMutation({
    mutationFn: api.logout,
    onSettled: () => {
      qc.clear();
      qc.setQueryData(qk.me, null);
      navigate("/login");
    },
  });

  const navCls = ({ isActive }: { isActive: boolean }) => cx("sidebar-item", isActive && "active");

  return (
    <nav
      className="sidebar"
      // Links, and notifications (their panel is portaled but React events still bubble here).
      onClick={(e) => (e.target as HTMLElement).closest("a, .notification") && onNavigate?.()}
    >
      <div className="sidebar-brand">
        <span className="brand-mark" aria-hidden="true">
          <Icon name="board" />
        </span>
        <span>Agent Task Runner</span>
      </div>

      <div className="sidebar-section">
        <NotificationsBell />
        <NavLink to="/monitor" className={navCls}>
          <Icon name="server" /> <span>Monitor</span>
          <ActiveRunsBadge />
        </NavLink>
      </div>

      <div className="sidebar-section sidebar-projects">
        <div className="sidebar-heading">
          <span>Projects</span>
          {me.role === "admin" && (
            <NavLink to="/projects/new" className="btn-icon" title="New project" aria-label="New project">
              <Icon name="plus" size={14} />
            </NavLink>
          )}
        </div>
        {projects?.map((p) => (
          <NavLink
            key={p.id}
            to={`/p/${p.id}`}
            className={() => cx("sidebar-item", p.id === pid && "active")}
          >
            <span className="project-initial" aria-hidden="true">
              {p.name.slice(0, 1).toUpperCase()}
            </span>
            <span className="truncate">{p.name}</span>
          </NavLink>
        ))}
        {projects && projects.length === 0 && <p className="muted sidebar-empty">No projects yet</p>}
      </div>

      <div className="sidebar-footer">
        <NavLink to="/account" className="sidebar-user" title="Account">
          <span className="avatar" aria-hidden="true">
            {(me.display_name || me.username).slice(0, 1).toUpperCase()}
          </span>
          <span className="truncate">{me.display_name || me.username}</span>
        </NavLink>
        <button className="btn-icon" onClick={() => logout.mutate()} title="Log out" aria-label="Log out">
          <Icon name="logout" />
        </button>
      </div>

      {buildSha && (
        <div className="sidebar-build truncate" title={`Build ${buildSha}`}>
          build <span className="sidebar-build-sha">{buildSha}</span>
        </div>
      )}
    </nav>
  );
}

function ActiveRunsBadge() {
  const { data } = useQuery({ queryKey: qk.monitorRuns, queryFn: api.monitorRuns, refetchInterval: 30_000 });
  const n = data?.filter((r) => r.status !== "queued").length ?? 0;
  if (!n) return null;
  return (
    <span className="badge badge-muted" title={`${n} agent run${n === 1 ? "" : "s"} in progress`}>
      {n}
    </span>
  );
}
